import React, {
    createContext,
    memo,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useMatch } from "react-router-dom";

import { setAnalyticsUserProperties } from "@/constants/amplitude";
import { IS_FEMALE } from "@/constants/isFemale";
import { Routes as AppRoutes } from "@/constants/routes";
import { Genders } from "@/types/gender";

import { getFunnelTypeForAnalytics } from "./analyticsFunnelType";
import { getIsFunnelApplovin } from "./applovinFunnelUtils";
import { UrlParams } from "./urlParams";

const FEMALE_PREFIX = "female";
const MALE_PREFIX = "man";

const useGenderFromRoute = (): Genders | null => {
    const location = useLocation();
    const genderMatch = location.pathname.match(/\/(male|female)/);

    return genderMatch ? (genderMatch[1] as Genders) : null;
};

const useGenderFromUrlParams = (): Genders | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const gender = urlParams.get(UrlParams.GENDER)?.toLowerCase();
    return gender ? (gender as Genders) : null;
};

export interface GenderContextType {
    gender: Genders;
    setGender: React.Dispatch<React.SetStateAction<Genders>>;
    isFemale: boolean;
    prefix: string;
}

export const defaultGender = IS_FEMALE ? Genders.Female : Genders.Male;
const defaultPrefix = IS_FEMALE ? FEMALE_PREFIX : MALE_PREFIX;

export const GenderContext = createContext<GenderContextType>({
    gender: defaultGender,
    setGender: () => null,
    isFemale: IS_FEMALE,
    prefix: defaultPrefix,
});

const GenderProvider = ({ children }: PropsWithChildren) => {
    const genderFromPath = useGenderFromRoute();
    const genderFromUrlParams = useGenderFromUrlParams();

    const isFriends = useMatch({
        path: `/:both/${AppRoutes.Friends}`,
        end: false,
    });

    const [gender, setGender] = useState<Genders>(
        genderFromPath || genderFromUrlParams || defaultGender
    );

    const isFemale = useMemo(() => gender === Genders.Female, [gender]);
    const prefix = useMemo(
        () => (gender === Genders.Female ? FEMALE_PREFIX : MALE_PREFIX),
        [gender]
    );

    useEffect(() => {
        if (gender) {
            let domain = window.location.hostname;
            isFriends && (domain = `${domain}/${AppRoutes.Friends}`);

            setAnalyticsUserProperties({
                web_domain: domain,
                web_funnel_flow: getFunnelTypeForAnalytics(gender),
                web_gender: gender,
                web_funnel_applovin: getIsFunnelApplovin(),
            });
        }
    }, [isFriends, gender]);

    return (
        <GenderContext.Provider
            value={{
                gender,
                setGender,
                isFemale,
                prefix,
            }}
        >
            {children}
        </GenderContext.Provider>
    );
};

export const useGender = () => {
    return useContext(GenderContext);
};

export default memo(GenderProvider);
